import { useQuery } from '@tanstack/react-query';
import type {
  ItemDto,
  OrderBoard,
  OrderDetailDto,
  OrderStatus,
  OrderSummaryDto,
  OutboundOverview,
  Page,
  PartnerDto,
} from '@zenarty/shared';
import {
  AlarmClock,
  AlertTriangle,
  CalendarCheck,
  FileUp,
  PackageCheck,
  PauseCircle,
  Play,
  Plus,
  Send,
  Trash2,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api';
import { useAuth } from '../auth';
import { BarChart } from '../components/charts';
import { ImportDialog } from '../components/importer';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Stat,
  Tabs,
  type Tone,
} from '../components/kit';
import { Dialog } from '../components/overlays';
import { ItemPicker } from '../components/pickers';
import { DataTable, type Column } from '../components/table';
import { formatDate, formatNumber } from '../lib/format';
import { useAction, useClients, useSites } from '../lib/queries';
import { Progress } from './Inbound';

export const ORDER_TONE: Record<OrderStatus, Tone> = {
  open: 'neutral',
  on_hold: 'danger',
  backordered: 'warn',
  allocated: 'info',
  released: 'accent',
  picking: 'accent',
  picked: 'accent',
  packed: 'ok',
  shipped: 'ok',
  cancelled: 'neutral',
};
export const ORDER_LABEL: Record<OrderStatus, string> = {
  open: 'Open',
  on_hold: 'On hold',
  backordered: 'Backordered',
  allocated: 'Allocated',
  released: 'Released',
  picking: 'Picking',
  picked: 'Picked',
  packed: 'Packed',
  shipped: 'Shipped',
  cancelled: 'Cancelled',
};

export function OrderStatusBadge({ status }: { status: OrderStatus }) {
  return (
    <Badge tone={ORDER_TONE[status]} dot>
      {ORDER_LABEL[status]}
    </Badge>
  );
}

export const cutoffLabel = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return sameDay
    ? `Today ${time}`
    : `${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} ${time}`;
};

export function RiskBadge({
  o,
}: {
  o: { atRisk: boolean; late: boolean; riskReason: string | null };
}) {
  if (o.late) return <Badge tone="danger">Late</Badge>;
  if (o.atRisk) return <Badge tone="warn">At risk</Badge>;
  return null;
}

function SiteSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { can } = useAuth();
  const sites = useSites(can('site:read'));
  if ((sites.data?.items.length ?? 0) < 2) return null;
  return (
    <select
      className="select"
      aria-label="Site"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">All sites</option>
      {(sites.data?.items ?? []).map((s) => (
        <option key={s.id} value={s.id}>
          {s.name}
        </option>
      ))}
    </select>
  );
}

type Tab =
  | 'active'
  | 'exceptions'
  | 'open'
  | 'allocated'
  | 'released'
  | 'picking'
  | 'picked'
  | 'packed'
  | 'shipped'
  | 'all';

export function OrdersPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const sites = useSites(can('site:read'));
  const clients = useClients();
  const [siteId, setSiteId] = useState('');
  const [tab, setTab] = useState<Tab>('active');
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const pageSize = 50;
  const params = new URLSearchParams({ limit: String(pageSize), offset: String(page * pageSize) });
  if (siteId) params.set('siteId', siteId);
  if (tab !== 'all') params.set('status', tab);
  if (search) params.set('q', search);
  const list = useQuery({
    queryKey: ['orders', 'list', params.toString()],
    queryFn: () =>
      api<Page<OrderSummaryDto> & { counts: Record<string, number> }>(`/orders?${params}`),
    refetchInterval: 30_000,
  });
  const ov = useQuery({
    queryKey: ['orders', 'overview', siteId],
    queryFn: () => api<OutboundOverview>(`/outbound/overview${siteId ? `?siteId=${siteId}` : ''}`),
    refetchInterval: 60_000,
  });
  const o = ov.data;
  const counts = list.data?.counts ?? {};
  const releaseSite = siteId || sites.data?.items[0]?.id || '';
  const releaseReady = useAction(
    () =>
      api<{ orders: number; lists: number }>('/orders/release-ready', {
        method: 'POST',
        body: { siteId: releaseSite },
      }),
    {
      invalidate: [['orders'], ['picking']],
      success: (r) =>
        r.orders
          ? `Released ${r.orders} order(s) into ${r.lists} pick list(s)`
          : 'No allocated orders waiting',
    },
  );
  const multiClient = (clients.data?.items.length ?? 0) > 1;
  const columns: Column<OrderSummaryDto>[] = [
    {
      id: 'number',
      header: 'Order',
      cell: (r) => (
        <div>
          <div className="primary-cell mono">{r.number}</div>
          <div className="sub">
            {[
              r.type === 'b2b' ? 'B2B' : r.type === 'parcel' ? 'Parcel' : r.type.replace('_', '-'),
              r.poNumber ? `PO ${r.poNumber}` : null,
              r.waveNumber,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
      ),
    },
    ...(multiClient
      ? [{ id: 'client', header: 'Client', cell: (r: OrderSummaryDto) => r.clientName }]
      : []),
    {
      id: 'shipto',
      header: 'Ship to',
      cell: (r) => (
        <div>
          <div>{r.retailerName ?? r.shipToName ?? '—'}</div>
          <div className="sub">
            {[r.retailerName ? r.shipToName : null, r.shipToCity].filter(Boolean).join(' · ')}
          </div>
        </div>
      ),
    },
    {
      id: 'cutoff',
      header: 'Cut-off',
      cell: (r) => (
        <div>
          <div className={r.late ? 'danger-text' : r.atRisk ? 'warn-text' : ''}>
            {cutoffLabel(r.cutoffAt)}
          </div>
          <div className="sub">
            {r.mustArriveBy
              ? `MABD ${formatDate(`${r.mustArriveBy}T00:00:00`)}`
              : (r.carrierName ?? '')}
          </div>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <OrderStatusBadge status={r.status} />
          <RiskBadge o={r} />
        </div>
      ),
    },
    {
      id: 'progress',
      header: 'Picked',
      cell: (r) =>
        r.status === 'shipped' ? (
          <Progress value={r.shippedUnits} total={r.units} />
        ) : (
          <Progress value={r.pickedUnits} total={r.units} />
        ),
    },
    {
      id: 'flags',
      header: '',
      align: 'right',
      cell: (r) =>
        r.holdReason && r.status === 'on_hold' ? (
          <span className="small danger-text">{r.holdReason}</span>
        ) : r.warnings.length ? (
          <span className="small warn-text" title={r.warnings.join('\n')}>
            <AlertTriangle aria-hidden style={{ width: 14, height: 14, verticalAlign: -2 }} />{' '}
            {r.warnings[0]}
          </span>
        ) : null,
    },
  ];
  return (
    <div className="stack-lg">
      <PageHeader
        title="Orders"
        description="Every order from intake to ship confirm. Allocation follows FEFO/FIFO and retailer shelf-life rules; cut-offs are in the site's time zone."
        actions={
          <>
            <SiteSelect value={siteId} onChange={setSiteId} />
            {can('outbound:manage') ? (
              <Button
                icon={Play}
                loading={releaseReady.isPending}
                disabled={!releaseSite}
                onClick={() => releaseReady.mutate(undefined)}
              >
                Release ready orders
              </Button>
            ) : null}
            {can('outbound:submit') ? (
              <>
                <Button icon={FileUp} onClick={() => setImporting(true)}>
                  Import CSV
                </Button>
                <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                  New order
                </Button>
              </>
            ) : null}
          </>
        }
      />
      <div className="grid-stats">
        <Stat
          label="Due today"
          icon={CalendarCheck}
          value={o ? formatNumber(o.dueToday) : '—'}
          foot={o ? `${formatNumber(o.shippedToday)} shipped today` : null}
        />
        <Stat
          label="At risk / late"
          icon={AlarmClock}
          tone={o?.late ? 'danger' : o?.atRisk ? 'warn' : 'ok'}
          value={o ? `${o.atRisk} / ${o.late}` : '—'}
          foot="Against cut-off"
          onClick={() => void navigate('/outbound/board')}
        />
        <Stat
          label="Units shipped today"
          icon={Send}
          tone="ok"
          value={o ? formatNumber(o.unitsShippedToday) : '—'}
          foot={o ? `${formatNumber(o.openPickTasks)} picks open` : null}
        />
        <Stat
          label="Exceptions"
          icon={PauseCircle}
          tone={o && o.onHold + o.backordered ? 'warn' : 'accent'}
          value={o ? formatNumber(o.onHold + o.backordered) : '—'}
          foot={o ? `${o.onHold} on hold · ${o.backordered} backordered` : null}
          onClick={() => setTab('exceptions')}
        />
        <Stat
          label="Fill rate (30 d)"
          icon={TrendingUp}
          tone="ok"
          value={o && o.fillRate30d !== null ? `${o.fillRate30d}%` : '—'}
          foot={o && o.onTime30d !== null ? `${o.onTime30d}% shipped on time` : 'No shipments yet'}
        />
      </div>
      {o && o.shippedTrend.some((t) => t.orders) ? (
        <Card title="Shipped per day" sub="Orders shipped, last 14 days">
          <BarChart
            series={[{ key: 'orders', label: 'Orders shipped', color: 'var(--ok)' }]}
            data={o.shippedTrend.map((t) => ({ label: t.date.slice(5), orders: t.orders }))}
            height={140}
          />
        </Card>
      ) : null}
      <div>
        <Tabs
          value={tab}
          onChange={(t) => {
            setTab(t);
            setPage(0);
          }}
          tabs={[
            { id: 'active', label: 'Active' },
            {
              id: 'exceptions',
              label: 'Exceptions',
              count: (counts.on_hold ?? 0) + (counts.backordered ?? 0),
            },
            { id: 'open', label: 'Open', count: counts.open ?? 0 },
            { id: 'allocated', label: 'Allocated', count: counts.allocated ?? 0 },
            { id: 'released', label: 'Released', count: counts.released ?? 0 },
            { id: 'picking', label: 'Picking', count: counts.picking ?? 0 },
            { id: 'picked', label: 'Picked', count: counts.picked ?? 0 },
            { id: 'packed', label: 'Packed', count: counts.packed ?? 0 },
            { id: 'shipped', label: 'Shipped', count: counts.shipped ?? 0 },
            { id: 'all', label: 'All' },
          ]}
        />
        <DataTable
          columns={columns}
          rows={list.data?.items ?? []}
          rowKey={(r) => r.id}
          loading={list.isLoading}
          onRowClick={(r) => void navigate(`/outbound/orders/${r.id}`)}
          searchPlaceholder="Search order, PO or ship-to"
          server={{
            total: list.data?.total ?? 0,
            page,
            pageSize,
            onPage: setPage,
            search,
            onSearch: (q) => {
              setSearch(q);
              setPage(0);
            },
          }}
          empty={
            <EmptyState icon={PackageCheck} title="No orders here">
              Orders arrive by API, EDI 940, CSV or the client portal.
            </EmptyState>
          }
        />
      </div>
      {creating ? (
        <CreateOrderDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => void navigate(`/outbound/orders/${id}`)}
        />
      ) : null}
      {importing ? <ImportOrders onClose={() => setImporting(false)} /> : null}
    </div>
  );
}

function ImportOrders({ onClose }: { onClose: () => void }) {
  const { me } = useAuth();
  const sites = useSites();
  const clients = useClients();
  const [siteId, setSiteId] = useState('');
  const [clientId, setClientId] = useState(me?.clientId ?? '');
  return (
    <ImportDialog
      title="Import orders"
      description="One row per order line; rows with the same order_number become one order. Carrier and retailer are partner codes."
      endpoint="/orders/import"
      scope={{ siteId, clientId }}
      scopeReady={Boolean(siteId && clientId)}
      picker={
        <div className="form-grid">
          <Field label="Site">
            <select className="select" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">Choose…</option>
              {(sites.data?.items ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Client">
            <select
              className="select"
              value={clientId}
              disabled={Boolean(me?.clientId)}
              onChange={(e) => setClientId(e.target.value)}
            >
              <option value="">Choose…</option>
              {(clients.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      }
      templateHeaders={[
        'order_number',
        'sku',
        'qty',
        'uom',
        'type',
        'ship_by',
        'cutoff',
        'must_arrive_by',
        'ship_to_name',
        'address1',
        'address2',
        'city',
        'state',
        'postal_code',
        'country',
        'phone',
        'carrier',
        'retailer',
        'po_number',
        'lot',
      ]}
      templateExample={[
        'SO-10001',
        'SKU-100',
        2,
        'EA',
        'parcel',
        '2026-11-03',
        '',
        '',
        'Jamie Rivera',
        '410 W Pine St',
        '',
        'Tempe',
        'AZ',
        '85281',
        'US',
        '',
        'UPSN',
        '',
        '',
        '',
      ]}
      templateName="orders-template.csv"
      invalidate="orders"
      onClose={onClose}
    />
  );
}

interface DraftLine {
  key: number;
  item: ItemDto | null;
  qty: string;
  uom: string;
}

function CreateOrderDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { me } = useAuth();
  const sites = useSites();
  const clients = useClients();
  const [siteId, setSiteId] = useState('');
  const [clientId, setClientId] = useState(me?.clientId ?? '');
  const [type, setType] = useState<'parcel' | 'b2b'>('parcel');
  const [number, setNumber] = useState('');
  const [shipBy, setShipBy] = useState(new Date().toISOString().slice(0, 10));
  const [cutoff, setCutoff] = useState('');
  const [mabd, setMabd] = useState('');
  const [po, setPo] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [retailerId, setRetailerId] = useState('');
  const [addr, setAddr] = useState({
    name: '',
    line1: '',
    line2: '',
    city: '',
    region: '',
    postalCode: '',
    country: 'US',
    phone: '',
  });
  const [lines, setLines] = useState<DraftLine[]>([{ key: 1, item: null, qty: '', uom: '' }]);
  const partners = useQuery({
    queryKey: ['partners', 'all'],
    queryFn: () => api<Page<PartnerDto>>('/trading-partners?limit=200'),
  });
  const carriers = (partners.data?.items ?? []).filter((p) => p.type === 'carrier');
  const retailers = (partners.data?.items ?? []).filter(
    (p) => p.type === 'retailer' && (!p.clientId || p.clientId === clientId),
  );
  const set = (key: number, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const create = useAction(
    () =>
      api<OrderDetailDto>('/orders', {
        method: 'POST',
        body: {
          siteId,
          clientId,
          number: number.trim(),
          type,
          shipByDate: shipBy,
          cutoffTime: cutoff || null,
          mustArriveBy: mabd || null,
          poNumber: po || null,
          carrierId: carrierId || null,
          retailerId: retailerId || null,
          shipTo: {
            ...addr,
            line2: addr.line2 || null,
            region: addr.region.toUpperCase() || null,
            phone: addr.phone || null,
          },
          lines: lines
            .filter((l) => l.item && Number(l.qty))
            .map((l) => ({ itemId: l.item!.id, qty: Number(l.qty), uom: l.uom || undefined })),
        },
      }),
    {
      invalidate: [['orders']],
      success: (r) => `Order ${r.number} created — ${ORDER_LABEL[r.status].toLowerCase()}`,
      onSuccess: (r) => {
        onClose();
        onCreated(r.id);
      },
    },
  );
  const a = (k: keyof typeof addr) => ({
    value: addr[k],
    onChange: (e: { target: { value: string } }) => setAddr((x) => ({ ...x, [k]: e.target.value })),
  });
  const ready =
    siteId &&
    clientId &&
    number.trim() &&
    addr.name &&
    addr.line1 &&
    addr.city &&
    addr.postalCode &&
    lines.some((l) => l.item && Number(l.qty) > 0);
  return (
    <Dialog
      wide
      title="New order"
      description="Validated on submit (address, items, dates, duplicates) and allocated immediately when stock allows."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!ready}
            onClick={() => create.mutate(undefined)}
          >
            Create order
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="form-grid-3">
          <Field label="Client">
            <select
              className="select"
              value={clientId}
              disabled={Boolean(me?.clientId)}
              onChange={(e) => setClientId(e.target.value)}
            >
              <option value="">Choose…</option>
              {(clients.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Ship from">
            <select className="select" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">Choose…</option>
              {(sites.data?.items ?? [])
                .filter(
                  (s) =>
                    !clientId ||
                    (
                      clients.data?.items.find((c) => c.id === clientId)?.siteIds ?? [s.id]
                    ).includes(s.id),
                )
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Type">
            <div className="segmented">
              <button
                type="button"
                className={type === 'parcel' ? 'active' : ''}
                onClick={() => setType('parcel')}
              >
                Parcel (B2C)
              </button>
              <button
                type="button"
                className={type === 'b2b' ? 'active' : ''}
                onClick={() => setType('b2b')}
              >
                B2B retail
              </button>
            </div>
          </Field>
          <Field label="Order number">
            <input
              className="input mono"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="SO-10001"
            />
          </Field>
          <Field label="Ship by">
            <input
              className="input"
              type="date"
              value={shipBy}
              onChange={(e) => setShipBy(e.target.value)}
            />
          </Field>
          <Field label="Cut-off" hint="Blank = client default">
            <input
              className="input"
              type="time"
              value={cutoff}
              onChange={(e) => setCutoff(e.target.value)}
            />
          </Field>
          <Field label="Carrier">
            <select
              className="select"
              value={carrierId}
              onChange={(e) => setCarrierId(e.target.value)}
            >
              <option value="">—</option>
              {carriers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          {type === 'b2b' ? (
            <>
              <Field label="Retailer" hint="Its shelf-life rule applies">
                <select
                  className="select"
                  value={retailerId}
                  onChange={(e) => setRetailerId(e.target.value)}
                >
                  <option value="">—</option>
                  {retailers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.minShelfLifeDays ? ` (${c.minShelfLifeDays} d min)` : ''}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Must arrive by">
                <input
                  className="input"
                  type="date"
                  value={mabd}
                  onChange={(e) => setMabd(e.target.value)}
                />
              </Field>
              <Field label="Customer PO">
                <input className="input" value={po} onChange={(e) => setPo(e.target.value)} />
              </Field>
            </>
          ) : null}
        </div>
        <div className="section-title">Ship to</div>
        <div className="form-grid-3">
          <Field label={type === 'b2b' ? 'Store / DC name' : 'Name'}>
            <input className="input" {...a('name')} />
          </Field>
          <Field label="Address" className="span-2">
            <input className="input" {...a('line1')} />
          </Field>
          <Field label="City">
            <input className="input" {...a('city')} />
          </Field>
          <Field label="State">
            <input className="input" maxLength={2} {...a('region')} />
          </Field>
          <Field label="ZIP / postal code">
            <input className="input" {...a('postalCode')} />
          </Field>
        </div>
        <div className="stack-sm">
          <div className="section-title">Lines</div>
          {lines.map((l, i) => (
            <div
              key={l.key}
              className="row"
              style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}
            >
              <span className="muted small num" style={{ width: 20, paddingTop: 10 }}>
                {i + 1}
              </span>
              <div style={{ flex: '2 1 260px' }}>
                <ItemPicker
                  value={l.item}
                  clientId={clientId || undefined}
                  onChange={(item) => set(l.key, { item, uom: item?.baseUom ?? '' })}
                />
              </div>
              <input
                className="input num"
                style={{ width: 90 }}
                placeholder="Qty"
                inputMode="numeric"
                value={l.qty}
                onChange={(e) => set(l.key, { qty: e.target.value })}
              />
              <select
                className="select"
                style={{ width: 90 }}
                aria-label="Unit"
                value={l.uom}
                onChange={(e) => set(l.key, { uom: e.target.value })}
              >
                {(l.item?.uoms ?? [{ uom: 'EA' }]).map((u) => (
                  <option key={u.uom} value={u.uom}>
                    {u.uom}
                  </option>
                ))}
              </select>
              <Button
                variant="ghost"
                size="sm"
                icon={Trash2}
                aria-label="Remove line"
                disabled={lines.length === 1}
                onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
              />
            </div>
          ))}
          <div>
            <Button
              size="sm"
              icon={Plus}
              onClick={() =>
                setLines((ls) => [...ls, { key: Date.now(), item: null, qty: '', uom: '' }])
              }
            >
              Add line
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

const BOARD_COLUMNS: { label: string; statuses: OrderStatus[] }[] = [
  { label: 'Not released', statuses: ['open', 'on_hold', 'backordered', 'allocated'] },
  { label: 'Picking', statuses: ['released', 'picking'] },
  { label: 'Packing', statuses: ['picked'] },
  { label: 'Ready to ship', statuses: ['packed'] },
  { label: 'Shipped', statuses: ['shipped'] },
];

/** Live order status board by cut-off (PRD §6 "Kanban of orders"). */
export function OrderBoardPage() {
  const navigate = useNavigate();
  const [siteId, setSiteId] = useState('');
  const q = useQuery({
    queryKey: ['orders', 'board', siteId],
    queryFn: () => api<OrderBoard>(`/orders/board${siteId ? `?siteId=${siteId}` : ''}`),
    refetchInterval: 30_000,
  });
  const groups = q.data?.groups ?? [];
  const late = groups.reduce((a, g) => a + g.late, 0);
  const risk = groups.reduce((a, g) => a + g.atRisk, 0);
  return (
    <div className="stack-lg">
      <PageHeader
        title="Order board"
        description={`Every order due in the next two days, by cut-off. Refreshes every 30 seconds.${late || risk ? ` ${late} late, ${risk} at risk.` : ''}`}
        actions={<SiteSelect value={siteId} onChange={setSiteId} />}
      />
      {!groups.length && !q.isLoading ? (
        <Card>
          <EmptyState icon={CalendarCheck} title="Nothing due">
            No orders have a cut-off in the next two days.
          </EmptyState>
        </Card>
      ) : null}
      {groups.map((g) => (
        <Card
          key={g.cutoffAt}
          title={`Cut-off ${cutoffLabel(g.cutoffAt)}`}
          sub={`${g.orders.length} order(s)`}
          actions={
            <div className="row" style={{ gap: 6 }}>
              {g.late ? <Badge tone="danger">{g.late} late</Badge> : null}
              {g.atRisk ? <Badge tone="warn">{g.atRisk} at risk</Badge> : null}
            </div>
          }
        >
          <div className="board">
            {BOARD_COLUMNS.map((col) => {
              const cards = g.orders.filter((o) => col.statuses.includes(o.status));
              return (
                <div key={col.label} className="board-col">
                  <div className="board-col-head">
                    {col.label} <span className="muted">{cards.length}</span>
                  </div>
                  {cards.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      className={`board-card ${o.late ? 'late' : o.atRisk ? 'risk' : ''}`}
                      onClick={() => void navigate(`/outbound/orders/${o.id}`)}
                    >
                      <div className="row" style={{ justifyContent: 'space-between', gap: 6 }}>
                        <span className="mono" style={{ fontWeight: 600 }}>
                          {o.number}
                        </span>
                        <OrderStatusBadge status={o.status} />
                      </div>
                      <div className="small muted">
                        {o.clientName} · {o.shipToName ?? '—'}
                      </div>
                      <div className="small">
                        {formatNumber(o.pickedUnits)}/{formatNumber(o.units)} picked
                        {o.carrierName ? ` · ${o.carrierName}` : ''}
                      </div>
                      {o.riskReason ? (
                        <div className={`small ${o.late ? 'danger-text' : 'warn-text'}`}>
                          {o.riskReason}
                        </div>
                      ) : null}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
}
